"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import type { DbAnnouncementCategory, DbAnnouncementIncidentState } from "@/types/db";
import {
  CATEGORY_ICON,
  CATEGORY_ICON_CLASS,
  CATEGORY_LABEL,
  selectFeaturedAnnouncement,
} from "@/components/announcements/announcement-shared";

// Minimal slice of announcement_posts the chip needs.
type FeaturedChipPost = {
  id: string;
  category: DbAnnouncementCategory;
  title: string;
  incident_state: DbAnnouncementIncidentState | null;
  is_featured: boolean;
};

async function loadFeatured(): Promise<FeaturedChipPost | null> {
  const { data, error } = await supabase
    .from("announcement_posts")
    .select("id, category, title, incident_state, is_featured")
    .eq("active", true)
    .order("created_at", { ascending: false });
  if (error || !data) return null;
  return selectFeaturedAnnouncement(data as FeaturedChipPost[]);
}

// chat-view mounts the chip twice (floating top-right cluster + the desktop
// ChatHeader, each shown at different breakpoints) — share one fetch per page
// load instead of hitting Supabase from every instance.
let featuredFetch: Promise<FeaturedChipPost | null> | null = null;

function fetchFeatured(): Promise<FeaturedChipPost | null> {
  // Swallow failures — the chat page must never break because of this chip.
  featuredFetch ??= loadFeatured().catch(() => null);
  return featuredFetch;
}

/**
 * Small pill linking to the currently-featured announcement (same selection
 * logic as the announcements page), tinted with that category's color.
 * Renders nothing when no announcement is featured or the fetch fails.
 * Mobile shows the category icon only; sm+ adds the truncated title.
 */
export function FeaturedAnnouncementChip({ className }: { className?: string }) {
  const router = useRouter();
  const [post, setPost] = useState<FeaturedChipPost | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchFeatured().then((result) => {
      if (!cancelled) setPost(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!post) return null;

  const Icon = CATEGORY_ICON[post.category];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          aria-label={`Featured ${CATEGORY_LABEL[post.category].toLowerCase()}: ${post.title}`}
          className={cn(
            "flex h-7 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            CATEGORY_ICON_CLASS[post.category],
            className
          )}
          onClick={() => router.push(`/announcements/${post.id}`)}
          type="button"
        >
          <Icon size={14} />
          {/* Mobile: icon-only chip; sm+ shows the truncated title */}
          <span className="hidden max-w-40 truncate sm:block">{post.title}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {CATEGORY_LABEL[post.category]}: {post.title}
      </TooltipContent>
    </Tooltip>
  );
}
